import type { Constructor, Container } from '@packages/core/container';

import { StsBackendClient } from '@packages/clients';
import { ANALYTICS_EVENTS, AnalyticsService } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';

import { StsBackendOperation } from '../../../sts-backend-operation';

const logger = getLogger('techline.cid-cvn');

/** Parameters for deleting a CID-CVN draft case. */
export type DeleteCidCvnCaseParams = {
    /** The case number to delete. */
    caseNumber: string;
};

/**
 * Delete a CID-CVN draft case and log an analytics event on success.
 * Uses the dedicated CID-CVN delete endpoint, which is separate from
 * the standard techline case delete.
 */
export class DeleteCidCvnCase extends StsBackendOperation {
    /**
     * Register this operation with additional dependency on AnalyticsService.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- static `this` in an abstract class is not assignable to Constructor without a cast
        container.register(this as any as Constructor, [StsBackendClient, AnalyticsService]);
    }

    /** @param analyticsService - the analytics service for logging events */
    private analyticsService: AnalyticsService;

    /**
     * @param client - the backend API client
     * @param analyticsService - the analytics service for logging events
     */
    constructor(client: StsBackendClient, analyticsService: AnalyticsService) {
        super(client);
        this.analyticsService = analyticsService;
    }

    /**
     * Delete a CID-CVN draft case by case number.
     *
     * @param params - the case to delete
     * @returns true if the deletion succeeded
     */
    async exec(params: DeleteCidCvnCaseParams): Promise<boolean> {
        try {
            const { caseNumber } = params;
            // Call the CID-CVN-specific delete endpoint
            const response = await this.client.deleteCidCvnCase({ caseNumber });

            // Log analytics event on successful deletion
            if (response?.success) {
                this.analyticsService.logEvent(ANALYTICS_EVENTS.USER_DELETES_TECHLINE_DRAFT);
                return true;
            }
            return false;
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to delete CID CVN case';
            logger.error('Failed to delete the CID/CVN case', { error: e });
            throw new Error(message);
        }
    }
}
